import {
  BMButton,
  BMCard,
  BMCheckbox,
  BMDivider,
  BMInputField,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemCrossClose from "@traveloka/icon-kit-web/react/IcSystemCrossClose";
import IcSystemInvite from "@traveloka/icon-kit-web/react/IcSystemInvite";
import IcSystemSearch from "@traveloka/icon-kit-web/react/IcSystemSearch";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Pressable, StyleSheet } from "react-native";
import BMDropdownInput from "../../../../components/DropdownInput";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { useTravelerPicker } from "../../../../providers/api/TravelerPickerContext";
import { DropdownItem, Traveler } from "../../../../providers/api/types";
import { formatMessage } from "../../../../utils";
import StickyContainer from "../../Sticky/StickyContainer";

type Props = {
  closeDialog: () => void;
  openNonEmployeePicker: () => void;
  selectedEmployees: Traveler[];
  setSelectedEmployees: Dispatch<SetStateAction<Traveler[]>>;
  numOfAdults: number;
  numOfChildren: number;
  handleSave: () => void;
};

export default function EmployeeList(props: Props) {
  const {
    closeDialog,
    openNonEmployeePicker,
    selectedEmployees,
    setSelectedEmployees,
    numOfAdults,
    numOfChildren,
    handleSave,
  } = props;
  const { CorporateHotelSearchForm, CorporateProductSearchForm } =
    useMobileContentResource();
  const bmTheme = useBMTheme();

  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedDivision, setSelectedDivision] = useState<DropdownItem>({
    label: "",
    value: "",
  });
  const [displayedTravelers, setDisplayedTravelers] = useState<Traveler[]>([]);
  const [listHeaderText, setListHeaderText] = useState<string>("");

  const {
    travelers,
    recentlyPickedTraveler,
    fetchDivisionList,
    divisions,
    handleDivisionChange,
    handleSearchValueChange,
  } = useTravelerPicker();

  useEffect(() => {
    fetchDivisionList();
  }, []);

  useEffect(() => {
    initEmployeeList();
  }, [selectedEmployees, travelers]);

  useEffect(() => {
    handleDivisionChange(selectedDivision);
  }, [selectedDivision]);

  useEffect(() => {
    handleSearchValueChange(searchValue);
  }, [searchValue]);

  function initEmployeeList() {
    if (searchValue || selectedDivision.value) {
      setListHeaderText(CorporateHotelSearchForm.searchResult);
      setDisplayedTravelers(travelers);
      return;
    }

    if (selectedEmployees.length) {
      setListHeaderText(CorporateHotelSearchForm.selectedEmployees);
      setDisplayedTravelers(selectedEmployees);
      return;
    }

    setListHeaderText(CorporateProductSearchForm.recentlySelected);
    setDisplayedTravelers(recentlyPickedTraveler);
  }

  function handleCheckboxChange(value: boolean, traveler: Traveler) {
    if (value) {
      setSelectedEmployees((prev) => [...prev, traveler]);
      return;
    }
    setSelectedEmployees((prev) =>
      prev.filter(
        (selectedEmployee) =>
          selectedEmployee.employeeId !== traveler.employeeId
      )
    );
  }

  function isTravelerSelected(traveler: Traveler) {
    return selectedEmployees.some(
      (selectedEmployee) => selectedEmployee.employeeId === traveler.employeeId
    );
  }

  const hasNonEmployees = !!(numOfAdults + numOfChildren);

  return (
    <StickyContainer
      headerContent={
        <EmployeeListHeader
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          closeDialog={closeDialog}
          bmTheme={bmTheme}
          divisions={divisions}
          selectedDivision={selectedDivision}
          setSelectedDivision={setSelectedDivision}
        />
      }
      footerContent={
        <EmployeeListFooter
          handleSaveTravelerPicker={handleSave}
          numOfAdults={numOfAdults}
          numOfChildren={numOfChildren}
          selectedEmployees={selectedEmployees}
        />
      }
      headerStyle={styles.headerContainer}
      contentStyle={styles.contentContainer}
    >
      <BMView style={styles.container}>
        <BMView style={styles.listContainer}>
          <BMText variant="uiLargeLi" ink="BMStyleTextInkSecondary">
            {listHeaderText}
          </BMText>
          <BMView style={styles.checkBoxContainer} spacing="lg">
            {displayedTravelers.map((traveler) => (
              <BMCheckbox
                key={traveler.employeeId}
                value={isTravelerSelected(traveler)}
                onChange={(value: boolean) => {
                  handleCheckboxChange(value, traveler);
                }}
                label={traveler.fullname}
                description={[traveler.division, traveler.email]
                  .filter(Boolean)
                  .join(" - ")}
                disabled={
                  !isTravelerSelected(traveler) &&
                  numOfAdults + numOfChildren + selectedEmployees.length >= 30
                }
              />
            ))}
          </BMView>
        </BMView>
        <BMView style={styles.nonEmployeeContainer}>
          <BMText
            style={styles.nonEmployeeText}
            ink="BMStyleTextInkSecondary"
            variant="uiLargeLi"
          >
            {CorporateHotelSearchForm.addPeopleOutside}
          </BMText>
          <Pressable onPress={openNonEmployeePicker}>
            <BMView
              row
              justify="center"
              spacing="md"
              style={[
                styles.nonEmployeeButton,
                {
                  borderColor: hasNonEmployees
                    ? bmTheme.BMComponents.BMStyleLineBordercolorActive
                    : bmTheme.BMComponents.BMStyleLineBordercolorSubtle,
                },
                hasNonEmployees && {
                  backgroundColor:
                    bmTheme.BMComponents.BMStyleSurfaceBgcolorActive,
                },
              ]}
            >
              <IcSystemInvite
                color={bmTheme.BMComponents.BMStyleIconInkPrimary}
                accentColor={bmTheme.BMComponents.BMStyleIconInkPrimary}
                width={16}
                height={16}
              />
              <BMText variant="uiNormalLi" ink="BMStyleTextInkPrimary">
                {hasNonEmployees
                  ? formatMessage(CorporateHotelSearchForm.nonEmployeesAdded, {
                      num: numOfAdults + numOfChildren,
                    })
                  : CorporateHotelSearchForm.nonEmployeeEnablementCheckbox}
              </BMText>
            </BMView>
          </Pressable>
        </BMView>
      </BMView>
    </StickyContainer>
  );
}

type HeaderProps = {
  closeDialog: () => void;
  bmTheme: ReturnType<typeof useBMTheme>;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  divisions: DropdownItem[];
  selectedDivision: DropdownItem;
  setSelectedDivision: Dispatch<SetStateAction<DropdownItem>>;
};

function EmployeeListHeader(props: HeaderProps) {
  const {
    closeDialog,
    bmTheme,
    searchValue,
    setSearchValue,
    divisions,
    selectedDivision,
    setSelectedDivision,
  } = props;
  const { CorporateHotelSearchForm } = useMobileContentResource();
  return (
    <BMView>
      <BMView row justify="between" align="center">
        <BMText variant="titleSmall" ink="BMStyleTextInkPrimary">
          {CorporateHotelSearchForm.emptyGuestText}
        </BMText>
        <Pressable onPress={closeDialog}>
          <IcSystemCrossClose
            color={bmTheme.BMComponents.BMStyleIconInkPrimary}
          />
        </Pressable>
      </BMView>
      <BMView style={styles.header}>
        <BMText variant="titleSmall" ink="BMStyleTextInkSecondary">
          {CorporateHotelSearchForm.employeeList}
        </BMText>
        <BMView row spacing="sm" style={styles.inputs}>
          <BMView style={styles.search}>
            <BMInputField
              LeadingIcon={IcSystemSearch}
              value={searchValue}
              onChange={(e) => setSearchValue(e.nativeEvent.text)}
              label=""
              placeholder={CorporateHotelSearchForm.searchEmployee}
              buttonInsetProps={
                searchValue
                  ? {
                      label: CorporateHotelSearchForm.clear,
                      onPress: () => setSearchValue(""),
                    }
                  : undefined
              }
            />
          </BMView>
          <BMView style={styles.dropdown}>
            <BMDropdownInput
              value={selectedDivision.label}
              placeholder={CorporateHotelSearchForm.all}
              items={divisions}
              onItemSelect={setSelectedDivision}
            />
          </BMView>
        </BMView>
      </BMView>
      <BMView style={styles.divider}>
        <BMDivider type="SUBTLE" />
      </BMView>
    </BMView>
  );
}

type FooterProps = {
  handleSaveTravelerPicker: () => void;
  numOfAdults: number;
  numOfChildren: number;
  selectedEmployees: Traveler[];
};

function EmployeeListFooter(props: FooterProps) {
  const {
    handleSaveTravelerPicker,
    numOfAdults,
    numOfChildren,
    selectedEmployees,
  } = props;
  const { CorporateHotelSearchForm, CorporateProductSearchForm } =
    useMobileContentResource();
  return (
    <BMCard style={styles.footer} layout="SPACIOUS" elevation="FLOAT">
      <BMView row justify="between" align="center">
        <BMView>
          <BMText variant="titleSmall" ink="BMStyleTextInkPrimary">
            {CorporateHotelSearchForm.selectedGuests}
          </BMText>
          <BMText variant="uiNormal" ink="BMStyleTextInkSecondary">
            {formatMessage(CorporateProductSearchForm.nonEmployeeAdultUnit, {
              num: numOfAdults + selectedEmployees.length,
            })}
            ,{" "}
            {formatMessage(CorporateProductSearchForm.nonEmployeeChildUnit, {
              num: numOfChildren,
            })}
          </BMText>
        </BMView>
        <BMButton
          role="PRIMARY"
          size="LARGE"
          label={CorporateHotelSearchForm.continue}
          onPress={handleSaveTravelerPicker}
        />
      </BMView>
    </BMCard>
  );
}
const styles = StyleSheet.create({
  container: {
    height: "100%",
  },
  header: {
    marginTop: BMPrimitives.BMSize.lg,
    zIndex: 2,
  },
  headerContainer: {
    zIndex: 2,
  },
  contentContainer: {
    zIndex: 1,
  },
  footer: {
    margin: -BMPrimitives.BMSize.md,
  },
  inputs: {
    marginTop: BMPrimitives.BMSize.lg,
  },
  search: {
    width: "60%",
  },
  dropdown: {
    width: "35%",
  },
  divider: {
    marginTop: BMPrimitives.BMSize.lg,
    zIndex: 1,
  },
  listContainer: {
    marginTop: BMPrimitives.BMSize.lg,
    marginLeft: BMPrimitives.BMSize.sm,
  },
  checkBoxContainer: {
    marginTop: BMPrimitives.BMSize.lg,
  },
  nonEmployeeContainer: {
    paddingBottom: BMPrimitives.BMSize.md,
  },
  nonEmployeeText: {
    marginTop: BMPrimitives.BMSize.xl3,
  },
  nonEmployeeButton: {
    marginTop: BMPrimitives.BMSize.xl,
    paddingVertical: BMPrimitives.BMSize.md,
    borderWidth: BMPrimitives.BMBorder.width.thick,
    borderRadius: BMPrimitives.BMRadius.md,
    marginBottom: BMPrimitives.BMSize.xs,
  },
});
